import { useEffect, useMemo, useRef, useState } from "react";
import { snoozeEntryKey, type MailSnoozeEntry } from "@/lib/snooze/constants";
import {
  getFolderSnapshot,
  patchFolderSnapshotRemoveUids,
  subscribeFolderSnapshot,
} from "@/lib/mail/inboxFolderBus";
import { notifySnoozeWoke } from "@/lib/snooze/mailSnoozeWakeEvents";
import { syncFolderFromServerCache, syncFolderHeaders } from "@/lib/mail/folderWarmup";
import { clearMutationTombstoneUids } from "@/lib/mail/mailMutationHold";
import {
  mergeSnoozeEntryWithHeader,
  resolveSnoozeEntryHeader,
} from "@/lib/snooze/snoozeDisplayMessages";
import type { MailMessageSummary } from "@/lib/types/mail";

type Options = {
  holdFolder?: string | null;
  enabled?: boolean;
};

/**
 * Liste « En attente » : MySQL uniquement, enrichie par les en-têtes IMAP hold si dispo.
 */
export function useSnoozedListRows(
  accountId: number | undefined,
  snoozes: MailSnoozeEntry[],
  options?: Options
) {
  const holdFolder = options?.holdFolder?.trim() || null;
  const enabled = options?.enabled !== false && accountId != null;
  const [revision, setRevision] = useState(0);
  const prevSnoozesRef = useRef<MailSnoozeEntry[]>([]);

  const snoozeKey = useMemo(
    () =>
      snoozes
        .map((s) => `${s.folder}\0${s.uid}\0${s.holdUid ?? ""}\0${s.until}`)
        .sort()
        .join("|"),
    [snoozes]
  );

  /** Hold IMAP + dossiers d’origine (Gmail overlay) — pour lu/non lu / sync. */
  const watchedFolders = useMemo(() => {
    const folders = new Set<string>();
    if (holdFolder) folders.add(holdFolder);
    for (const entry of snoozes) {
      const folder = entry.holdFolder?.trim() || entry.folder?.trim();
      if (folder) folders.add(folder);
    }
    return [...folders].sort();
  }, [holdFolder, snoozes]);

  useEffect(() => {
    if (!enabled || accountId == null || watchedFolders.length === 0) return;

    let cancelled = false;
    void Promise.all(
      watchedFolders.map((folder) => syncFolderHeaders(accountId, folder))
    ).then(() => {
      if (!cancelled) setRevision((value) => value + 1);
    });

    return () => {
      cancelled = true;
    };
  }, [accountId, enabled, snoozeKey, watchedFolders]);

  useEffect(() => {
    if (!enabled || accountId == null || watchedFolders.length === 0) return;

    const unsubs = watchedFolders.map((folder) =>
      subscribeFolderSnapshot(accountId, folder, () => {
        setRevision((value) => value + 1);
      })
    );
    return () => {
      for (const unsub of unsubs) unsub();
    };
  }, [accountId, enabled, watchedFolders]);

  useEffect(() => {
    if (!enabled || accountId == null) return;

    const prev = prevSnoozesRef.current;
    if (prev.length > snoozes.length) {
      const now = Date.now();
      const currentKeys = new Set(snoozes.map((s) => snoozeEntryKey(s.folder, s.uid)));
      const removed = prev.filter((s) => {
        if (currentKeys.has(snoozeEntryKey(s.folder, s.uid))) return false;
        const untilMs = new Date(s.until).getTime();
        return Number.isFinite(untilMs) && untilMs <= now + 15_000;
      });

      if (removed.length === 0) {
        prevSnoozesRef.current = snoozes;
        return;
      }

      notifySnoozeWoke(
        accountId,
        removed.map((entry) => ({
          folder: entry.folder,
          uid: entry.uid,
          subject: entry.subject,
          holdFolder: entry.holdFolder,
        }))
      );

      for (const entry of removed) {
        clearMutationTombstoneUids(accountId, entry.folder, [entry.uid]);
        const hold = entry.holdFolder?.trim();
        if (hold) {
          const holdUid = entry.holdUid ?? entry.uid;
          patchFolderSnapshotRemoveUids(accountId, hold, [holdUid]);
        }
        void (async () => {
          await syncFolderFromServerCache(accountId, entry.folder);
          await syncFolderHeaders(accountId, entry.folder);
        })();
      }
      if (holdFolder) {
        void syncFolderFromServerCache(accountId, holdFolder);
        void syncFolderHeaders(accountId, holdFolder);
      }
    }

    prevSnoozesRef.current = snoozes;
  }, [accountId, enabled, holdFolder, snoozeKey, snoozes]);

  const messages = useMemo(() => {
    void revision;

    if (accountId == null) return [];

    const rows: MailMessageSummary[] = [];

    for (const entry of snoozes) {
      const headerFolder = entry.holdFolder?.trim() || entry.folder;
      const folderHeaders = getFolderSnapshot(accountId, headerFolder)?.messages ?? [];
      const header = resolveSnoozeEntryHeader(entry, folderHeaders);
      rows.push(mergeSnoozeEntryWithHeader(entry, header, accountId));
    }

    return rows.sort((a, b) => {
      const ta = new Date(a.snoozeUntil ?? a.date ?? 0).getTime();
      const tb = new Date(b.snoozeUntil ?? b.date ?? 0).getTime();
      return ta - tb;
    });
  }, [accountId, revision, snoozes]);

  const bodyFolderByUid = useMemo(() => {
    const map = new Map<number, string>();
    for (const msg of messages) {
      if (msg.imapFolder) map.set(msg.uid, msg.imapFolder);
    }
    return map;
  }, [messages]);

  const bodyUidByUid = useMemo(() => {
    const map = new Map<number, number>();
    for (const msg of messages) {
      const bodyUid = msg.imapBodyUid ?? msg.uid;
      if (bodyUid > 0) map.set(msg.uid, bodyUid);
    }
    return map;
  }, [messages]);

  const snoozeUntilByUid = useMemo(() => {
    const map = new Map<number, string>();
    for (const msg of messages) {
      if (msg.snoozeUntil) map.set(msg.uid, msg.snoozeUntil);
    }
    return map;
  }, [messages]);

  return {
    messages,
    bodyFolderByUid,
    bodyUidByUid,
    snoozeUntilByUid,
    holdFolderCount: snoozes.length,
    loading: enabled && holdFolder != null && messages.length === 0 && snoozes.length === 0,
  };
}
